/**
 * Google Analytics 4 (GA4) utility functions for web user platform
 * Tracks events, page views, and user engagement
 * Excludes SUPERADMIN actions from analytics
 */

declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Initialize Google Analytics
 * Should be called once when the app loads
 */
export const initAnalytics = (): void => {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;

  if (!measurementId) {
    // Analytics is optional - don't break the app if not configured
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  const dataLayer = window.dataLayer;
  window.gtag = function gtag(...args: unknown[]) {
    dataLayer.push(args);
  };

  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Configure GA4
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    page_path: window.location.pathname + window.location.search,
    page_location: window.location.href,
  });
};

/**
 * Track a custom event
 * Excludes SUPERADMIN actions
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, string | number | boolean>,
  userRole?: string
): void => {
  // Skip analytics for SUPERADMIN
  if (userRole === 'SUPERADMIN') {
    return;
  }

  if (!window.gtag) {
    return;
  }

  window.gtag('event', eventName, eventParams);
};

/**
 * Track page view
 * Excludes SUPERADMIN actions
 */
export const trackPageView = (path: string, userRole?: string): void => {
  // Skip analytics for SUPERADMIN
  if (userRole === 'SUPERADMIN') {
    return;
  }

  if (!window.gtag) {
    return;
  }

  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!measurementId) {
    return;
  }

  window.gtag('config', measurementId, {
    page_path: path,
    page_location: window.location.origin + path,
  });
};

/**
 * Track feature usage
 */
export const trackFeatureUsage = (
  featureName: string,
  action: string,
  userRole?: string
): void => {
  trackEvent('feature_usage', {
    feature_name: featureName,
    action,
    event_category: 'engagement',
  }, userRole);
};

/**
 * Track user engagement
 */
export const trackEngagement = (
  engagementType: string,
  value?: number,
  userRole?: string
): void => {
  trackEvent('user_engagement', {
    engagement_type: engagementType,
    ...(value !== undefined && { value }),
    event_category: 'engagement',
  }, userRole);
};

